import { useState, useCallback, useMemo } from 'react'
import type { JSX } from 'react/jsx-runtime'
import { addMonths, format, parseISO, subMonths } from 'date-fns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useCases } from '@/features/cases/hooks/use-cases-simplified'
import { useChamber } from '@/hooks/use-chamber'
import { supabase } from '@/lib/supabase'
import type {
  CalendarDisplayEvent,
  CalendarProviderProps,
  RawCalendarEvent,
  RawCalendarEventWithDate,
  SaveEventParams,
  CreateEventParams,
} from '@/types'
import { CalendarContext } from '../hooks/useCalendar'

export const CalendarProvider = ({
  children,
}: CalendarProviderProps): JSX.Element => {
  const { user } = useAuth()
  const { chamber } = useChamber()
  const { cases } = useCases()
  const queryClient = useQueryClient()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState<string | undefined>()
  const [createForm, setCreateForm] = useState({
    case_id: '',
    description: '',
    event_date: format(new Date(), 'yyyy-MM-dd'),
    event_time: '',
    event_type: 'hearing',
    location: '',
    title: '',
  })
  const [editForm, setEditForm] = useState({
    location: '',
    status: 'scheduled',
    time: '',
    title: '',
  })

  // Fetch events query
  const {
    data: rawEvents = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['calendar-events', chamber?.id],
    queryFn: async (): Promise<RawCalendarEvent[]> => {
      if (!user || !chamber?.id) throw new Error('Authentication required')

      const { data, error } = await supabase
        .from('user_calendar_events')
        .select('*')
        .eq('chamber_id', chamber.id)
        .order('event_date', { ascending: true })

      if (error) throw error

      return data
    },
    enabled: Boolean(user) && Boolean(chamber?.id),
  })

  const refreshEvents = useCallback(async (): Promise<void> => {
    await refetch()
  }, [refetch])

  const calendarEvents = useMemo(() => {
    return rawEvents
      .filter((event): event is RawCalendarEventWithDate =>
        Boolean(event.event_date),
      )
      .map((event) => {
        return {
          caseNumber: event.case_number ?? undefined,
          caseTitle: event.case_title ?? undefined,
          clientName: event.client_name ?? undefined,
          date: parseISO(event.event_date),
          id: event.id ?? '',
          location: event.location ?? null,
          status: event.event_status ?? 'pending',
          time: event.event_time
            ? format(parseISO(`1970-01-01T${event.event_time}`), 'h:mm a')
            : undefined,
          title: event.title ?? '',
          type: event.event_type ?? 'other',
        }
      })
  }, [rawEvents])

  // Mutations
  const saveEventMutation = useMutation({
    mutationFn: async ({ event, editFormData }: SaveEventParams) => {
      if (!user) throw new Error('Authentication required')

      const updateData = {
        status: editFormData.status,
        title: editFormData.title,
        ...(event.type === 'hearing' && {
          event_time: editFormData.time,
          location: editFormData.location,
        }),
      }
      const { error } = await supabase
        .from('calendar_events')
        .update(updateData)
        .eq('id', event.id)

      if (error) throw error
    },
    onSuccess: () => {
      setEditingEvent(undefined)
      void queryClient.invalidateQueries({
        queryKey: ['calendar-events', chamber?.id],
      })
    },
    onError: () => {
      toast.error('Failed to save event')
    },
  })

  const createEventMutation = useMutation({
    mutationFn: async ({ createFormData, eventDate }: CreateEventParams) => {
      if (!user || !chamber?.id) throw new Error('Authentication required')

      const eventData = {
        ...createFormData,
        chamber_id: chamber.id,
        event_date: format(eventDate, 'yyyy-MM-dd'),
        entity_id: crypto.randomUUID(),
        entity_type: 'calendar_event',
      }
      const { error } = await supabase.from('calendar_events').insert(eventData)

      if (error) throw error
    },
    onSuccess: () => {
      setIsCreateModalOpen(false)
      setCreateForm({
        case_id: '',
        description: '',
        event_date: format(new Date(), 'yyyy-MM-dd'),
        event_time: '',
        event_type: 'hearing',
        location: '',
        title: '',
      })
      void queryClient.invalidateQueries({
        queryKey: ['calendar-events', chamber?.id],
      })
    },
    onError: () => {
      toast.error('Failed to create event')
    },
  })

  const getSelectedDateEvents = useCallback((): CalendarDisplayEvent[] => {
    return calendarEvents.filter(
      (event) =>
        format(event.date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'),
    )
  }, [calendarEvents, selectedDate])

  const nextMonth = useCallback(() => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }, [currentMonth])

  const prevMonth = useCallback(() => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }, [currentMonth])

  const startEditing = useCallback((event: CalendarDisplayEvent) => {
    setEditingEvent(event.id)
    setEditForm({
      location: event.location ?? '',
      status: event.status ?? '',
      time: event.time ?? '',
      title: event.title ?? '',
    })
  }, [])

  const cancelEditing = useCallback(() => {
    setEditingEvent(undefined)
    setEditForm({ location: '', status: 'scheduled', time: '', title: '' })
  }, [])

  const saveEvent = useCallback(
    async (event: CalendarDisplayEvent) => {
      return saveEventMutation.mutateAsync({ event, editFormData: editForm })
    },
    [saveEventMutation, editForm],
  )

  const createEvent = useCallback(async () => {
    return createEventMutation.mutateAsync({
      createFormData: createForm,
      eventDate: selectedDate,
    })
  }, [createEventMutation, createForm, selectedDate])

  const updateCreateForm = useCallback((field: string, val: string) => {
    setCreateForm((prev) => ({ ...prev, [field]: val }))
  }, [])

  const updateEditForm = useCallback((field: string, val: string) => {
    setEditForm((prev) => ({ ...prev, [field]: val }))
  }, [])

  const value = useMemo(() => {
    return {
      calendarEvents,
      isLoading,
      selectedDate,
      cases,
      currentMonth,
      isCreateModalOpen,
      editingEvent,
      createForm,
      editForm,
      refreshEvents,
      setSelectedDate,
      setCurrentMonth,
      nextMonth,
      prevMonth,
      setIsCreateModalOpen,
      startEditing,
      cancelEditing,
      saveEvent,
      createEvent,
      updateCreateForm,
      updateEditForm,
      getSelectedDateEvents,
    }
  }, [
    calendarEvents,
    isLoading,
    selectedDate,
    cases,
    currentMonth,
    isCreateModalOpen,
    editingEvent,
    createForm,
    editForm,
    refreshEvents,
    nextMonth,
    prevMonth,
    setIsCreateModalOpen,
    startEditing,
    cancelEditing,
    saveEvent,
    createEvent,
    updateCreateForm,
    updateEditForm,
    getSelectedDateEvents,
  ])

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  )
}
